import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { FileDown, ScrollText } from "lucide-react";

/** Iter122bm · SuperAdmin · Dialog "Genera contratto" per un singolo tenant. */
export default function GeneraContrattoButton({ tenant }) {
  const [open, setOpen] = useState(false);
  const [modelli, setModelli] = useState([]);
  const [busy, setBusy] = useState(false);
  const [piani, setPiani] = useState([]);  // Iter122bu · piani dal listino
  const [form, setForm] = useState({
    modello_id: "",
    piano_scelto: "",
    n_condomini: 0,
    sezioni_attive: "",
    servizi_extra: "",
    corrispettivo_netto: 0,
    modalita_pagamento: "Bonifico bancario",
    scadenza_giorni: 30,
    data_decorrenza: new Date().toISOString().slice(0, 10),
    prorata_giorni: 0,
    prorata_importo: 0,
    iban: "",
    legale_rappresentante: "",
    dpo_riferimento: "DPO nominato in fase di attivazione",
    luogo_firma: "Napoli",
    data_firma: "",
    ginius_sede: "Napoli",
    ginius_piva: "",
  });

  useEffect(() => {
    if (!open) return;
    api.get("/superadmin/contratti-modelli").then((r) => {
      const list = r.data || [];
      setModelli(list);
      const def = list.find((m) => m.is_default) || list[0];
      if (def) setForm((s) => ({ ...s, modello_id: def.id }));
    }).catch(() => {});
    // Iter122bu · piani dal listino
    api.get("/superadmin/piani").then((r) => {
      const list = (r.data || []).filter((p) => p.attivo !== false);
      setPiani(list);
      if (list.length > 0) {
        setForm((s) => s.piano_scelto ? s : { ...s, piano_scelto: list[0].nome });
      }
    }).catch(() => {});
  }, [open]);

  const genera = async () => {
    if (!form.modello_id) { toast.error("Seleziona un modello"); return; }
    setBusy(true);
    try {
      const res = await api.post(`/superadmin/tenants/${tenant.id}/contratto`, form, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `contratto-${tenant.id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Contratto generato · trasmettilo allo Studio per la firma");
      setOpen(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore generazione"); }
    setBusy(false);
  };

  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}
              data-testid={`genera-contratto-${tenant.id}`} className="h-9">
        <ScrollText className="w-4 h-4 mr-1" /> Contratto
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-[95vw] max-w-2xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader><DialogTitle className="font-display text-lg sm:text-xl">Genera contratto · {tenant.nome}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Modello contratto</Label>
              <select value={form.modello_id} onChange={(e) => setForm({ ...form, modello_id: e.target.value })}
                      data-testid="genera-modello"
                      className="h-11 w-full mt-1 border-2 border-slate-200 rounded-md px-3">
                <option value="">— seleziona —</option>
                {modelli.map((m) => <option key={m.id} value={m.id}>{m.titolo}{m.is_default ? " (default)" : ""}</option>)}
              </select>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Piano scelto</Label>
                <select value={form.piano_scelto} onChange={(e) => setForm({ ...form, piano_scelto: e.target.value })}
                        className="h-11 w-full mt-1 border-2 border-slate-200 rounded-md px-3" data-testid="genera-piano">
                  {piani.length === 0 && <option value="">— nessun piano in Listino —</option>}
                  {piani.map((p) => {
                    const prezzo = p.modello_prezzo === "per_condominio"
                      ? `€ ${(p.prezzo_per_condominio_annuo || 0).toFixed(2)}/cond·anno`
                      : `€ ${(p.prezzo || 0).toFixed(2)}/${p.ciclo || "anno"}`;
                    return <option key={p.id} value={p.nome}>{p.nome} · {prezzo}</option>;
                  })}
                </select>
              </div>
              <div>
                <Label>N. condòmini iniziali</Label>
                <Input type="number" value={form.n_condomini} onChange={(e) => setForm({ ...form, n_condomini: parseInt(e.target.value) || 0 })} className="h-11 mt-1" data-testid="genera-n-condomini" />
              </div>
              <div>
                <Label>Corrispettivo netto annuo (€)</Label>
                <Input type="number" step="0.01" value={form.corrispettivo_netto} onChange={(e) => setForm({ ...form, corrispettivo_netto: parseFloat(e.target.value) || 0 })} className="h-11 mt-1" data-testid="genera-corrispettivo" />
              </div>
              <div>
                <Label>Modalità pagamento</Label>
                <Input value={form.modalita_pagamento} onChange={(e) => setForm({ ...form, modalita_pagamento: e.target.value })} className="h-11 mt-1" />
              </div>
              <div>
                <Label>Data decorrenza</Label>
                <Input type="date" value={form.data_decorrenza} onChange={(e) => setForm({ ...form, data_decorrenza: e.target.value })} className="h-11 mt-1" />
              </div>
              <div>
                <Label>Scadenza pagamento (gg)</Label>
                <Input type="number" value={form.scadenza_giorni} onChange={(e) => setForm({ ...form, scadenza_giorni: parseInt(e.target.value) || 30 })} className="h-11 mt-1" />
              </div>
            </div>

            <div>
              <Label>Sezioni facoltative attivate (elenco separato da virgole)</Label>
              <Input value={form.sezioni_attive} onChange={(e) => setForm({ ...form, sezioni_attive: e.target.value })} className="h-11 mt-1" placeholder="Es. Credius, Sinistri, Report" />
            </div>
            <div>
              <Label>Servizi extra concordati</Label>
              <Input value={form.servizi_extra} onChange={(e) => setForm({ ...form, servizi_extra: e.target.value })} className="h-11 mt-1" />
            </div>

            <div className="pt-2 border-t space-y-3">
              <p className="text-sm font-bold text-slate-600">Dati contrattuali</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Legale rappresentante Studio</Label><Input value={form.legale_rappresentante} onChange={(e) => setForm({ ...form, legale_rappresentante: e.target.value })} className="h-11 mt-1" /></div>
              <div><Label>IBAN Studio</Label><Input value={form.iban} onChange={(e) => setForm({ ...form, iban: e.target.value })} className="h-11 mt-1" /></div>
              <div><Label>Luogo firma</Label><Input value={form.luogo_firma} onChange={(e) => setForm({ ...form, luogo_firma: e.target.value })} className="h-11 mt-1" /></div>
              <div><Label>Data firma (o vuoto = oggi)</Label><Input type="date" value={form.data_firma} onChange={(e) => setForm({ ...form, data_firma: e.target.value })} className="h-11 mt-1" /></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Sede Ginius</Label><Input value={form.ginius_sede} onChange={(e) => setForm({ ...form, ginius_sede: e.target.value })} className="h-11 mt-1" /></div>
              <div><Label>P.IVA Ginius</Label><Input value={form.ginius_piva} onChange={(e) => setForm({ ...form, ginius_piva: e.target.value })} className="h-11 mt-1" /></div>
            </div>
            </div>

            <div className="flex gap-2 pt-2">
              <Button onClick={genera} disabled={busy} data-testid="genera-conferma" className="h-11 bg-brand hover:bg-brand-dark">
                <FileDown className="w-4 h-4 mr-1" /> {busy ? "Generazione…" : "Scarica PDF"}
              </Button>
              <Button variant="outline" onClick={() => setOpen(false)} className="h-11">Annulla</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
